"use client";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import type { DayWords } from "@/lib/lumi-api";

const DAILY_GOAL = 2000;

/* Recharts renders raw SVG, so it can't inherit Tailwind text colors — axis
   ticks default to #666, which is 2.90:1 on the Ink card (invisible). Bind
   them to the app token instead so they flip with the theme.

   The two series colors are fixed hexes chosen to clear 3:1 (WCAG 1.4.11,
   non-text contrast) against BOTH card surfaces, since a single SVG fill has
   to survive either theme. The previous values (#90A955 goal, #9CA3AF bar)
   failed at 2.63:1 and 2.54:1 — on the light card they were designed for. */
const AXIS_COLOR = "var(--app-body)";
const GOAL_COLOR = "#6B8A3F"; // 3.94:1 paper / 4.22:1 ink
const BAR_COLOR = "#767F8C"; // 4.05:1 paper / 4.11:1 ink

export function WordsReadChart({ data }: { data: DayWords[] }) {
  // Keep the axis readable for light readers while still showing the goal line.
  const maxWords = Math.max(4000, ...data.map((d) => d.words));

  return (
    <div>
      <ResponsiveContainer width="95%" height={400}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
          <XAxis dataKey="day" tick={{ fill: AXIS_COLOR }} stroke={AXIS_COLOR} />
          <YAxis
            domain={[0, maxWords]}
            tick={{ fill: AXIS_COLOR }}
            stroke={AXIS_COLOR}
            tickFormatter={(value) =>
              value >= 1000 ? `${value / 1000}K` : `${value}`
            }
          />
          <Tooltip
            contentStyle={{
              background: "var(--app-surface)",
              border: "1px solid var(--app-border)",
              borderRadius: "0.5rem",
              color: "var(--app-text)",
            }}
            labelStyle={{ color: "var(--app-text)" }}
            cursor={{ fill: "var(--app-tinted)" }}
          />
          <ReferenceLine
            y={DAILY_GOAL}
            stroke={GOAL_COLOR}
            strokeWidth={4}
            strokeDasharray="5 5"
          />
          <Bar dataKey="words" radius={[4, 4, 0, 0]}>
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.words >= DAILY_GOAL ? GOAL_COLOR : BAR_COLOR}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-2 text-center text-sm text-app-body">
        Words Read This Week
      </div>
    </div>
  );
}
